import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import ExportModal from '../common/ExportModal';
import UploadModal from '../common/UploadModal';
import EditableCell from '../common/EditableCell';
import FilterDropdown from '../common/FilterDropdown';
import { EXPORT_CONFIGS } from '../../config/exportConfigs';
import { UPLOAD_CONFIGS } from '../../config/uploadConfigs';
import JobStatusMonitor from '../ProgressTracking/Piping/JobStatusMonitor';
import useResizableColumns from '../../hooks/useResizableColumns';
import useTableFilter from '../../hooks/useTableFilter';
import useTableSort from '../../hooks/useTableSort';
import { useToast } from '../../hooks/useToast';
import usePresence from '../../hooks/usePresence';
import ActiveViewers from '../Matrix/ActiveViewers';
import PurchasingAnalyticsDashboard from './PurchasingAnalyticsDashboard';

// Column definitions for the purchasing list table
const COLUMNS = [
  { key: 'mto_desc', label: 'MTO Desc', type: 'text', width: 150 },
  { key: 'mto_size', label: 'MTO Size', type: 'text', width: 100 },
  { key: 'mto_quan', label: 'MTO Quan', type: 'number', width: 90 },
  { key: 'mto_sche', label: 'MTO Sche', type: 'text', width: 100 },
  { key: 'mto_mate', label: 'MTO Mate', type: 'text', width: 100 },
  { key: 'coo', label: 'COO', type: 'text', width: 80 },
  { key: 'ad_percent', label: 'AD %', type: 'number', width: 70 },
  { key: 'cv_percent', label: 'CV %', type: 'number', width: 70 },
  { key: 'section_232_percent', label: 'Section 232 %', type: 'number', width: 110 },
  { key: 'section_301_percent', label: 'Section 301 %', type: 'number', width: 110 },
  { key: 'reciprocal_percent', label: 'Reciprocal %', type: 'number', width: 100 },
  { key: 'qty', label: 'Qty', type: 'number', width: 70 },
  { key: 'unit_price', label: 'Unit Price', type: 'number', width: 100 },
  { key: 'total_price_before_tariff', label: 'Total Before Tariff', type: 'number', width: 140 },
  { key: 'total_price_after_tariff', label: 'Total After Tariff', type: 'number', width: 140 },
];

// Format value for display (number columns show locale-formatted decimals)
const formatPurchasingValue = (val, column) => {
  if (val === null || val === undefined || val === '') return '-';
  if (column.type === 'number') {
    return Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  return val;
};

const PurchasingListLog = () => {
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { toast, showToast } = useToast();
  const [savingRows, setSavingRows] = useState(new Set());
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newRow, setNewRow] = useState({});
  const [showExportModal, setShowExportModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportJobId, setExportJobId] = useState(null);
  const downloadedJobsRef = useRef(new Set());

  const fetchItemsRef = useRef(null);
  const handleDataChange = useCallback(() => {
    if (fetchItemsRef.current) fetchItemsRef.current();
  }, []);

  // Real-time presence tracking (shows who's viewing this page)
  const { viewers, isConnected, connectionState, socketId } = usePresence('purchasingList', 'global', true, handleDataChange);

  const socketIdRef = useRef(null);
  useEffect(() => { socketIdRef.current = socketId; }, [socketId]);

  const syncHeaders = useCallback(() => {
    return socketIdRef.current ? { headers: { 'x-socket-id': socketIdRef.current } } : {};
  }, []);

  // Use reusable hooks for filtering and sorting
  const filterHook = useTableFilter({ columns: COLUMNS, items });
  const sortHook = useTableSort({ columns: COLUMNS });

  // Resizable columns
  const defaultWidths = useMemo(() => {
    const widths = {};
    COLUMNS.forEach(col => { widths[col.key] = col.width; });
    return widths;
  }, []);
  const { getColumnWidth, getResizeHandleProps } = useResizableColumns(
    'purchasingListLog_columnWidths',
    defaultWidths
  );

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        '/purchasing-list'
      );

      if (response.data.success) {
        setItems(response.data.data || []);
      } else {
        setError('Failed to load purchasing list data');
      }
    } catch (err) {
      console.error('Error fetching purchasing list data:', err);
      setError('Failed to load purchasing list data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts]);

  useEffect(() => { fetchItemsRef.current = fetchItems; }, [fetchItems]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);


  // Export handlers
  const handleExport = async () => {
    if (isExporting) return;
    try {
      setIsExporting(true);
      setShowExportModal(false);
      const response = await authenticatedApiCall(instance, accounts, 'POST', '/purchasing-list/export', null, syncHeaders());
      if (response.data.success) {
        setExportJobId(response.data.jobId);
        showToast(`Export started! Job ID: ${response.data.jobId}`, 'success');
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      setIsExporting(false);
      showToast(`Export error: ${error.message}`, 'error');
    }
  };

  const handleExportJobComplete = (jobData) => {
    showToast(`Export completed! ${jobData.processed_rows} records exported.`, 'success');
    setIsExporting(false);
    setExportJobId(null);
    if (jobData.job_id && !downloadedJobsRef.current.has(jobData.job_id)) {
      downloadedJobsRef.current.add(jobData.job_id);
      window.open(`/api/download/${jobData.job_id}`, '_blank');
    }
  };

  const handleExportJobError = (errorMessage) => {
    showToast(`Export failed: ${errorMessage}`, 'error');
    setIsExporting(false);
    setExportJobId(null);
  };

  const handleSaveCell = async (rowId, columnKey, newValue) => {
    setSavingRows(prev => new Set(prev).add(rowId));

    try {
      const column = COLUMNS.find(c => c.key === columnKey);
      let processedValue = newValue;
      
      if (column?.type === 'number') {
        processedValue = newValue === '' ? null : Number(newValue);
      }

      const response = await authenticatedApiCall(
        instance,
        accounts,
        'PUT',
        `/purchasing-list/${rowId}`,
        { [columnKey]: processedValue },
        syncHeaders()
      );

      if (response.data.success) {
        setItems(prev => prev.map(item => 
          item.id === rowId ? { ...item, [columnKey]: processedValue } : item
        ));
        showToast('Saved successfully', 'success');
      } else {
        throw new Error(response.data.message || 'Save failed');
      }
    } catch (err) {
      console.error('Error saving cell:', err);
      showToast(`Save failed: ${err.message}`, 'error');
      throw err;
    } finally {
      setSavingRows(prev => {
        const newSet = new Set(prev);
        newSet.delete(rowId);
        return newSet;
      });
    }
  };

  const handleAddNewRow = async () => {
    try {
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        '/purchasing-list',
        newRow,
        syncHeaders()
      );

      if (response.data.success) {
        setItems(prev => [response.data.data, ...prev]);
        setNewRow({});
        setIsAddingNew(false);
        showToast('New record added successfully', 'success');
      } else {
        throw new Error(response.data.message || 'Failed to add record');
      }
    } catch (err) {
      console.error('Error adding new record:', err);
      showToast(`Failed to add record: ${err.message}`, 'error');
    }
  };

  const handleDeleteRow = async (rowId) => {
    if (!window.confirm('Are you sure you want to delete this record?')) return;

    try {
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'DELETE',
        `/purchasing-list/${rowId}`,
        null,
        syncHeaders()
      );

      if (response.data.success) {
        setItems(prev => prev.filter(item => item.id !== rowId));
        showToast('Record deleted successfully', 'success');
      } else {
        throw new Error(response.data.message || 'Delete failed');
      }
    } catch (err) {
      console.error('Error deleting record:', err);
      showToast(`Delete failed: ${err.message}`, 'error');
    }
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && filterHook.openFilterColumn) {
        filterHook.setOpenFilterColumn(null);
        filterHook.setFilterSearch('');
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [filterHook.openFilterColumn, filterHook]);

  // Combined active filters check
  const hasActiveFilters = filterHook.hasActiveFilters || searchTerm;

  // Clear all filters including search
  const handleClearAll = useCallback(() => {
    filterHook.clearAllFilters();
    setSearchTerm('');
    sortHook.clearSort();
    showToast('All filters cleared', 'success');
  }, [filterHook, sortHook, showToast]);

  const filteredItems = useMemo(() => {
    let result = [...items];
    
    // Global search
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(item => COLUMNS.some(col => { 
        const val = item[col.key]; 
        return val !== null && val !== undefined && String(val).toLowerCase().includes(search); 
      }));
    }
    
    // Apply column filters
    result = filterHook.applyFilters(result);
    
    // Apply sorting
    result = sortHook.applySorting(result);
    
    return result;
  }, [items, searchTerm, filterHook.selectedValues, filterHook.textFilters, filterHook.numericFilters, filterHook.dateFilters, filterHook.applyFilters, sortHook.sortColumn, sortHook.sortDirection, sortHook.applySorting]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <div style={{
          display: 'inline-block',
          width: '48px',
          height: '48px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #007bff',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ marginTop: '1rem', color: '#666' }}>Loading purchasing list...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '2rem',
        backgroundColor: '#fff3cd',
        border: '1px solid #ffc107',
        borderRadius: '8px',
        color: '#856404'
      }}>
        <strong>⚠️ Error:</strong> {error}
        <button
          onClick={fetchItems}
          style={{
            marginLeft: '1rem',
            padding: '0.5rem 1rem',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Toast Notification */}
      {toast.show && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          padding: '1rem 1.5rem',
          backgroundColor: toast.type === 'success' ? '#28a745' : '#dc3545',
          color: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 9999,
          animation: 'slideIn 0.3s ease'
        }}>
          {toast.message}
        </div>
      )}

      {/* Header Bar */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1rem',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        marginBottom: '1rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, color: '#333' }}>🛒 Purchasing List</h3>
          <span style={{ 
            backgroundColor: '#6f42c1', 
            color: 'white', 
            padding: '4px 12px', 
            borderRadius: '20px',
            fontSize: '0.85rem'
          }}>
            {filteredItems.length} records
          </span>
          
          {/* Active Filters Bar */}
          {hasActiveFilters && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 8px', backgroundColor: '#d1fae5', borderRadius: '4px', fontSize: '12px', color: '#065f46', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 'bold' }}>Filters:</span>
              {searchTerm && <span style={{ padding: '2px 6px', backgroundColor: '#10b981', color: 'white', borderRadius: '3px', fontSize: '11px' }}>Search: "{searchTerm}"</span>}
              {filterHook.activeFilters.map((f, idx) => (
                <span key={idx} style={{ padding: '2px 6px', backgroundColor: '#10b981', color: 'white', borderRadius: '3px', fontSize: '11px' }}>
                  {f.label}: {f.type === 'values' ? `${f.count} selected` : f.type === 'text' ? `${f.mode} "${f.value}"` : f.description || f.value}
                </span>
              ))}
              <button onClick={handleClearAll} style={{ padding: '2px 8px', backgroundColor: '#065f46', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }}>✕ Clear All</button>
            </div>
          )}
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {/* Active Viewers - Real-time presence indicator */}
          <ActiveViewers
            viewers={viewers}
            isConnected={isConnected}
            connectionState={connectionState}
            maxVisible={4}
            showConnectionStatus={true}
          />
          
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: '8px 12px 8px 36px',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                width: '200px',
                fontSize: '0.9rem'
              }}
            />
            <span style={{
              position: 'absolute',
              left: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#6c757d'
            }}>🔍</span>
          </div>

          <button onClick={() => setShowAnalytics(true)} style={{ padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}>📊 Analytics</button>
          <button onClick={() => setShowExportModal(true)} style={{ padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}>📤 Export</button>
          <button onClick={() => setShowUploadModal(true)} style={{ padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}>📥 Import</button>
          <button onClick={() => setIsAddingNew(true)} style={{ padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}>➕ Add New</button>
          <button onClick={fetchItems} style={{ padding: '0.5rem 1rem', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '500' }}>🔄 Refresh</button>
        </div>
      </div>

      {/* Info Banner */}
      <div style={{
        backgroundColor: '#f3e5f5',
        padding: '0.75rem 1rem',
        borderRadius: '4px',
        marginBottom: '1rem',
        fontSize: '0.85rem',
        color: '#6a1b9a'
      }}>
        💡 <strong>Tip:</strong> Click any cell to edit. Changes are saved automatically.
      </div>

      {/* Add New Row Form */}
      {isAddingNew && (
        <div style={{
          backgroundColor: '#fff3cd',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '1rem',
          border: '1px solid #ffc107'
        }}>
          <h4 style={{ margin: '0 0 1rem 0', color: '#856404' }}>Add New Purchasing List Item</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '0.75rem' }}>
            {COLUMNS.slice(0, 10).map(col => (
              <div key={col.key}>
                <label style={{ fontSize: '0.8rem', color: '#666', display: 'block', marginBottom: '4px' }}>
                  {col.label}
                </label>
                <input
                  type={col.type === 'number' ? 'number' : 'text'}
                  step={col.type === 'number' ? '0.01' : undefined}
                  value={newRow[col.key] || ''}
                  onChange={(e) => setNewRow(prev => ({ ...prev, [col.key]: e.target.value }))}
                  style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box' }}
                />
              </div>
            ))}
          </div>
          <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={handleAddNewRow}
              style={{
                padding: '8px 16px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              ✓ Save
            </button>
            <button
              onClick={() => { setIsAddingNew(false); setNewRow({}); }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              ✕ Cancel
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div style={{
        flex: 1,
        overflow: 'auto',
        maxHeight: 'calc(100vh - 350px)',
        border: '1px solid #dee2e6',
        borderRadius: '8px',
        backgroundColor: 'white'
      }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '0.85rem',
          minWidth: '1800px'
        }}>
          <thead style={{
            position: 'sticky',
            top: 0,
            backgroundColor: '#343a40',
            color: 'white',
            zIndex: 10
          }}>
            <tr>
              <th style={{ 
                padding: '12px 8px', 
                textAlign: 'center',
                width: '60px',
                borderRight: '1px solid #5a32a3',
                position: 'sticky',
                left: 0,
                backgroundColor: '#343a40',
                zIndex: 11
              }}>
                Actions
              </th>
              {COLUMNS.map(col => {
                const colWidth = getColumnWidth(col.key, col.width);
                return (
                <th key={col.key} style={{ padding: '8px 4px', textAlign: col.type === 'number' ? 'right' : 'left', width: colWidth, minWidth: colWidth, maxWidth: colWidth, borderRight: '1px solid #495057', whiteSpace: 'nowrap', position: 'relative', userSelect: 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: col.type === 'number' ? 'flex-end' : 'flex-start', gap: '4px' }}>
                    <span onClick={() => sortHook.handleSort(col.key)} style={{ cursor: 'pointer', flex: 1 }} title="Click to sort">
                      {col.label}
                      {sortHook.sortColumn === col.key && <span style={{ marginLeft: '4px' }}>{sortHook.sortDirection === 'asc' ? '▲' : '▼'}</span>}
                    </span>
                    <span 
                      onClick={(e) => { e.stopPropagation(); filterHook.setOpenFilterColumn(filterHook.openFilterColumn === col.key ? null : col.key); }} 
                      style={{ cursor: 'pointer', opacity: filterHook.hasColumnFilter(col.key) ? 1 : 0.5, fontSize: '0.75rem', color: filterHook.hasColumnFilter(col.key) ? '#10b981' : 'white' }} 
                      title="Filter"
                    >
                      🔽
                    </span>
                  </div>
                  {filterHook.openFilterColumn === col.key && (
                    <FilterDropdown
                      column={col}
                      uniqueValues={filterHook.getUniqueValues(col.key)}
                      selectedValues={filterHook.selectedValues[col.key] || []}
                      textFilter={filterHook.textFilters[col.key] || null}
                      numericFilter={filterHook.numericFilters[col.key] || null}
                      dateFilter={filterHook.dateFilters[col.key] || null}
                      filterSearch={filterHook.filterSearch}
                      onToggleValue={filterHook.toggleValue}
                      onSelectAll={filterHook.selectAll}
                      onClearColumn={filterHook.clearColumn}
                      onApplyTextFilter={filterHook.applyTextFilter}
                      onApplyNumericFilter={filterHook.applyNumericFilter}
                      onApplyDateFilter={filterHook.applyDateFilter}
                      onClose={() => {
                        filterHook.setOpenFilterColumn(null);
                        filterHook.setFilterSearch('');
                      }}
                      onSearchChange={filterHook.setFilterSearch}
                    />
                  )}
                  <div {...getResizeHandleProps(col.key, colWidth)} />
                </th>
              );
              })}
            </tr>
          </thead>
          <tbody>
            {filteredItems.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length + 1} style={{ 
                  padding: '3rem', 
                  textAlign: 'center', 
                  color: '#6c757d' 
                }}>
                  {searchTerm ? 'No matching records found' : 'No purchasing list items. Click "Add New" to create one.'}
                </td>
              </tr>
            ) : (
              filteredItems.map((item, index) => (
                <tr 
                  key={item.id} 
                  style={{
                    backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa',
                    borderBottom: '1px solid #dee2e6'
                  }}
                >
                  <td style={{ 
                    padding: '4px 8px', 
                    textAlign: 'center',
                    borderRight: '1px solid #eee',
                    position: 'sticky',
                    left: 0,
                    backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa',
                    zIndex: 5
                  }}>
                    <button
                      onClick={() => handleDeleteRow(item.id)}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.75rem'
                      }}
                      title="Delete row"
                    >
                      🗑️
                    </button>
                  </td>
                  {COLUMNS.map(col => {
                    const colWidth = getColumnWidth(col.key, col.width);
                    return (
                    <td 
                      key={col.key} 
                      style={{ 
                        padding: '4px 8px',
                        textAlign: col.type === 'number' ? 'right' : 'left',
                        borderRight: '1px solid #eee',
                        backgroundColor: savingRows.has(item.id) ? '#fff3cd' : 'inherit',
                        width: colWidth,
                        minWidth: colWidth,
                        maxWidth: colWidth
                      }}
                    >
                      <EditableCell
                        value={item[col.key]}
                        row={item}
                        column={col}
                        onSave={handleSaveCell}
                        disabled={false}
                        formatValue={formatPurchasingValue}
                      />
                    </td>
                  );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>

      {/* Export Job Monitor */}
      {exportJobId && (
        <JobStatusMonitor
          jobId={exportJobId}
          onComplete={handleExportJobComplete}
          onError={handleExportJobError}
        />
      )}

      {/* Export Modal */}
      {showExportModal && (
        <ExportModal
          {...EXPORT_CONFIGS.purchasingList}
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          onExport={handleExport}
          isExporting={isExporting}
        />
      )}

      {/* Upload Modal */}
      <UploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadSuccess={() => {
          fetchItems();
          setShowUploadModal(false);
        }}
        onUploadError={(msg) => showToast(msg, 'error')}
        config={UPLOAD_CONFIGS.PURCHASING_LIST}
      />

      {/* Analytics Dashboard */}
      {showAnalytics && (
        <PurchasingAnalyticsDashboard
          data={items}
          onClose={() => setShowAnalytics(false)}
        />
      )}

    </div>
  );
};

export default PurchasingListLog;



